type Child = Node | string | number | null | undefined | false;

/** Tiny element builder. Text is always set as text (never HTML), so data can't inject markup. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | number | EventListener | undefined> | null = null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v === undefined || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
    else if (k === 'class') el.className = String(v);
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
  return el;
}

/** replaceChildren that skips null/false entries. */
export function fill(el: Element, ...children: Child[]): void {
  el.replaceChildren(...children.filter((c): c is Node | string | number => c !== null && c !== undefined && c !== false).map((c) => (typeof c === 'number' ? String(c) : c)));
}

export function $(sel: string): HTMLElement {
  const el = document.querySelector<HTMLElement>(sel);
  if (!el) throw new Error(`missing ${sel}`);
  return el;
}

export function formatTime(iso: string | null | undefined, lang: 'ar' | 'en', withSeconds = false): string {
  if (!iso) return '';
  // Latin digits everywhere, matching numbers printed on receipts and shown by staff.
  return new Date(iso).toLocaleTimeString(lang === 'ar' ? 'ar-KW-u-nu-latn' : 'en-GB', { hour: '2-digit', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}) });
}

/** How long something has been going on, staff-board style: "الآن", "12 د", "2 س 5 د", "1 يوم 3 س". */
export function formatElapsed(minutes: number, lang: 'ar' | 'en'): string {
  const m = Math.max(0, Math.floor(minutes));
  const [min, hr, day] = lang === 'ar' ? ['د', 'س', 'يوم'] : ['m', 'h', 'd'];
  const unit = (n: number, u: string) => (lang === 'ar' ? `${n} ${u}` : `${n}${u}`);
  if (m < 1) return lang === 'ar' ? 'الآن' : 'now';
  if (m < 60) return unit(m, min);
  if (m < 24 * 60) return [unit(Math.floor(m / 60), hr), m % 60 ? unit(m % 60, min) : ''].filter(Boolean).join(' ');
  const h = Math.floor((m % (24 * 60)) / 60);
  return [unit(Math.floor(m / (24 * 60)), day), h ? unit(h, hr) : ''].filter(Boolean).join(' ');
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}
